import { lazy } from "react";
import { Redirect, RouteProps } from "react-router-dom";

import { FeatureToggleKey } from "../types/featureToggle";

// Common
const Login = lazy(() => import("../pages/Login"));
const SetPassword = lazy(() => import("../pages/SetPassword"));
const NoMatch = lazy(() => import("../pages/NoMatch"));

const MainLayout = lazy(() => import("../components/layouts/MainLayout"));
const NoticeLayout = lazy(() => import("../components/layouts/NoticeLayout"));

// Live product
const LiveEvents = lazy(() => import("../pages/LiveEvents"));
const LiveEventDetail = lazy(() => import("../pages/LiveEventDetail"));
const LiveEventAnalytics = lazy(() => import("../pages/LiveEventAnalytics"));
const LiveEventVods = lazy(() => import("../pages/LiveEventVods"));
const LiveCreateVod = lazy(() => import("../pages/LiveCreateVod"));
const LiveControlRooms = lazy(() => import("../pages/LiveControlRooms"));
const LiveControlRoomDetail = lazy(() => import("../pages/LiveControlRoomDetail"));
const LiveStickers = lazy(() => import("../pages/LiveStickers"));
const LiveWordFilters = lazy(() => import("../pages/LiveWordFilters"));
const Media = lazy(() => import("../pages/Media"));
const MediaUpload = lazy(() => import("../pages/MediaUpload"));
const VodToLiveDetail = lazy(() => import("../pages/VodToLiveDetail"));
const Pricing = lazy(() => import("../pages/Pricing"));
const Order = lazy(() => import("../pages/Order"));
const Invoice = lazy(() => import("../pages/Invoice"));
const TermsOfService = lazy(() => import("../pages/TermsOfService"));
const OrgSetting = lazy(() => import("../pages/OrgSetting"));
const Logout = lazy(() => import("../pages/Logout"));

export interface RoutesProps extends RouteProps {
  isPrivate?: boolean;
  checkPermission?: boolean;
  feature?: FeatureToggleKey;
  redirect?: string;
  routes?: RoutesProps[];
}

const noticeRoutes: RoutesProps[] = [
  {
    isPrivate: true,
    path: "/pricing",
    component: Pricing,
  },
  {
    isPrivate: true,
    path: "/order",
    component: Order,
  },
  {
    isPrivate: true,
    path: "/invoice",
    component: Invoice,
  },
  {
    isPrivate: true,
    path: "/terms-of-service",
    component: TermsOfService,
  },
];

const liveRoutes: RoutesProps[] = [
  {
    path: "/live/",
    exact: true,
    component: (): JSX.Element => <Redirect to="/live/events" />,
  },
  {
    path: "/live/events",
    exact: true,
    component: LiveEvents,
  },
  {
    checkPermission: true,
    path: "/live/new-event",
    component: LiveEventDetail,
  },
  {
    path: "/live/events/:id/analytics",
    component: LiveEventAnalytics,
  },
  {
    path: "/live/events/:id/vods",
    exact: true,
    component: LiveEventVods,
  },
  {
    path: "/live/events/:id/vods/new-vod",
    exact: true,
    component: LiveCreateVod,
  },
  {
    path: "/live/events/vod2live/:id",
    component: VodToLiveDetail,
  },
  {
    path: "/live/events/:id",
    component: LiveEventDetail,
  },
  {
    path: "/live/control-rooms",
    exact: true,
    component: LiveControlRooms,
  },
  {
    path: "/live/control-rooms/:id",
    component: LiveControlRoomDetail,
  },
  {
    path: "/live/stickers/",
    component: LiveStickers,
    feature: "sticker",
  },
  {
    path: "/live/wordfilters/",
    component: LiveWordFilters,
    feature: "word-filter",
  },
  {
    path: "/live/media/upload",
    component: MediaUpload,
  },
  {
    checkPermission: true,
    path: "/live/media",
    component: Media,
  },
  {
    checkPermission: true,
    path: "/live/new-vod2live",
    component: VodToLiveDetail,
  },
];

const routes: RoutesProps[] = [
  {
    path: "/login",
    component: Login,
  },
  {
    path: "/set-password",
    component: SetPassword,
  },
  {
    path: "/",
    exact: true,
    component: (): JSX.Element => <Redirect to="/live" />,
  },
  {
    isPrivate: true,
    path: ["/pricing", "/order", "/invoice", "/terms-of-service"],
    component: NoticeLayout,
    routes: noticeRoutes,
  },
  {
    isPrivate: true,
    path: ["/live", "/org-setting"],
    component: MainLayout,
    routes: [
      ...liveRoutes,
      {
        path: "/org-setting",
        exact: true,
        component: OrgSetting,
      },
      {
        component: NoMatch,
      },
    ],
  },
  {
    path: "/logout",
    component: Logout,
  },
  {
    component: NoMatch,
  },
];

export default routes;
